import { useEffect, useRef } from 'react'
import { useWatch, useFormContext, type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
	Checkbox,
	FormControl, FormField, FormItem, FormLabel, FormMessage,
	Input, RadioGroup,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { type AddPropertyFormValues } from './schema'
import { CurrencyField } from '@/components/currency-field'
import { SectionHeading } from './section-heading'
import { RadioOption } from './form-options'

type UtilityType = 'ELECTRICITY' | 'WATER' | 'HEATING' | 'WASTE'
type CalcType = 'BY_METER' | 'PROPORTIONAL' | 'FIXED_PER_SQM' | 'FIXED_AMOUNT' | 'COEFFICIENT' | 'INCLUDED'

const CALCULATION_OPTIONS = [
	{ value: 'BY_METER',      labelKey: 'properties.utilities.calcTypes.byMeter' },
	{ value: 'PROPORTIONAL',  labelKey: 'properties.utilities.calcTypes.proportional' },
	{ value: 'FIXED_PER_SQM', labelKey: 'properties.utilities.calcTypes.fixedPerSqm' },
	{ value: 'FIXED_AMOUNT',  labelKey: 'properties.utilities.calcTypes.fixedAmount' },
	{ value: 'COEFFICIENT',   labelKey: 'properties.utilities.calcTypes.coefficient' },
	{ value: 'INCLUDED',      labelKey: 'properties.utilities.calcTypes.included' },
] as const

const PAID_BY_OPTIONS = [
	{ value: 'TENANT',   labelKey: 'properties.utilities.paidByOptions.tenant' },
	{ value: 'LANDLORD', labelKey: 'properties.utilities.paidByOptions.landlord' },
] as const

const AREA_CALC_TYPES: CalcType[] = ['PROPORTIONAL', 'FIXED_PER_SQM', 'COEFFICIENT']

/**
 * What a section falls back to when switched back on and nothing was chosen
 * before it was switched off. Mirrors DEFAULT_VALUES in the schema.
 */
const DEFAULT_CALC: Record<UtilityType, CalcType> = {
	ELECTRICITY: 'BY_METER',
	WATER:       'PROPORTIONAL',
	HEATING:     'FIXED_PER_SQM',
	WASTE:       'FIXED_AMOUNT',
}

function UtilitySection({
	control,
	type,
	titleKey,
	showPaidBy = false,
	lockedCalc,
}: {
	control: Control<AddPropertyFormValues>
	type: UtilityType
	titleKey: string
	showPaidBy?: boolean
	lockedCalc?: CalcType
}) {
	const { t } = useTranslation()
	const form = useFormContext<AddPropertyFormValues>()
	const watched = useWatch({ control, name: `utilities.${type}.calculation` }) as CalcType | undefined
	const calculation = lockedCalc ?? watched
	const billed = calculation !== undefined
	// So switching a section off and back on returns the method that was
	// chosen, instead of silently resetting it to the default.
	const lastCalc = useRef<CalcType>(DEFAULT_CALC[type])
	useEffect(() => {
		if (watched) lastCalc.current = watched
	}, [watched])

	const isByMeter    = calculation === 'BY_METER'
	const needsCoeff   = calculation === 'COEFFICIENT'
	const needsRate    = calculation === 'FIXED_PER_SQM'
	const needsFixed   = calculation === 'FIXED_AMOUNT'
	const needsCurrency = calculation === 'FIXED_PER_SQM' || calculation === 'FIXED_AMOUNT'

	// Silently carry propertyArea from Step 2 totalArea for area-based calc types
	useEffect(() => {
		if (!AREA_CALC_TYPES.includes(calculation as CalcType)) return
		const totalArea = form.getValues('totalArea')
		if (totalArea && !form.getValues(`utilities.${type}.propertyArea`)) {
			form.setValue(`utilities.${type}.propertyArea`, totalArea)
		}
	}, [calculation]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<section className="flex flex-col gap-5">
			<SectionHeading>{t(titleKey)}</SectionHeading>

			{/* Whether the utility is settled at all, asked separately from how.
			    It used to be the first entry of the calculation list — «Не
			    тарифицируется» sitting among «По счётчику» and «За метр», reading
			    as a method when it means there is no method. Waste already asked
			    it as a checkbox, so the same question had two different controls
			    in one step. */}
			{!lockedCalc && (
				<FormField
					control={control}
					name={`utilities.${type}.calculation`}
					render={({ field }) => (
						<FormItem className="flex flex-row items-center gap-2 space-y-0">
							<FormControl>
								<Checkbox
									checked={billed}
									onCheckedChange={(checked) =>
										field.onChange(checked ? lastCalc.current : undefined)
									}
								/>
							</FormControl>
							<FormLabel className="font-[500] cursor-pointer">
								{t('properties.utilities.billed')}
							</FormLabel>
						</FormItem>
					)}
				/>
			)}

			{/* Calculation type [+ who pays, for BY_METER utilities] */}
			{!lockedCalc && billed && (
			<div className="flex gap-2 items-start">
				<FormField
					control={control}
					name={`utilities.${type}.calculation`}
					render={({ field }) => (
						<FormItem className="flex-1">
							<FormLabel>{t('properties.utilities.calcType')}</FormLabel>
							<Select value={field.value} onValueChange={field.onChange}>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder={t('properties.utilities.notSelected')} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{CALCULATION_OPTIONS.map((opt) => (
										<SelectItem key={opt.value} value={opt.value}>
											{t(opt.labelKey)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
				{showPaidBy && isByMeter && (
					<FormField
						control={control}
						name={`utilities.${type}.paidBy`}
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.utilities.paidBy')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder={t('properties.utilities.notSelected')} />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{PAID_BY_OPTIONS.map((opt) => (
											<SelectItem key={opt.value} value={opt.value}>
												{t(opt.labelKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
			</div>
			)}

			{/* BY_METER: hasSeparateMeter + meterNumber */}
			{isByMeter && (
				<div className="flex gap-2 items-start">
					<FormField
						control={control}
						name={`utilities.${type}.hasSeparateMeter`}
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.utilities.separateMeter')}</FormLabel>
								<FormControl>
									<RadioGroup
										value={field.value === undefined ? undefined : field.value ? 'YES' : 'NO'}
										onValueChange={(v) => field.onChange(v === 'YES')}
										className="flex flex-wrap gap-2"
									>
										<RadioOption value="YES" id={`${type}-sep-YES`} label={t('common.yes')} />
										<RadioOption value="NO"  id={`${type}-sep-NO`}  label={t('common.no')} />
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={control}
						name={`utilities.${type}.meterNumber`}
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.utilities.meterNumber')}</FormLabel>
								<FormControl>
									<Input placeholder="12344" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
			)}

			{/* COEFFICIENT: coefficient value */}
			{needsCoeff && (
				<FormField
					control={control}
					name={`utilities.${type}.coefficient`}
					render={({ field }) => (
						<FormItem className="w-[21.125rem]">
							<FormLabel>{t('properties.utilities.coefficientValue')}</FormLabel>
							<FormControl>
								<Input type="number" step="0.01" placeholder="2" {...field} value={field.value ?? ''} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			)}

			{/* FIXED_PER_SQM: ratePerSqm + currency */}
			{needsRate && (
				<div className="flex gap-2 items-start max-w-[22.4375rem]">
					<FormField
						control={control}
						name={`utilities.${type}.ratePerSqm`}
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.utilities.ratePerSqm')}</FormLabel>
								<FormControl>
									<Input type="number" placeholder="50" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					{needsCurrency && <CurrencyField label={t('properties.utilities.currency')} className="flex-1" />}
				</div>
			)}

			{/* FIXED_AMOUNT: fixedAmount + currency */}
			{needsFixed && (
				<div className="flex gap-2 items-start max-w-[22.4375rem]">
					<FormField
						control={control}
						name={`utilities.${type}.fixedAmount`}
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.utilities.amount')}</FormLabel>
								<FormControl>
									<Input type="number" placeholder="1000" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					{needsCurrency && <CurrencyField label={t('properties.utilities.currency')} className="flex-1" />}
				</div>
			)}
		</section>
	)
}

function WasteSection({ control }: { control: Control<AddPropertyFormValues> }) {
	const { t } = useTranslation()
	const form = useFormContext<AddPropertyFormValues>()
	const calculation = useWatch({ control, name: 'utilities.WASTE.calculation' })
	const enabled = calculation === 'FIXED_AMOUNT'

	return (
		<section className="flex flex-col gap-5">
			<SectionHeading>{t('properties.utilities.waste')}</SectionHeading>
			<FormField
				control={control}
				name="utilities.WASTE.calculation"
				render={({ field }) => (
					<FormItem className="flex flex-row items-center gap-2 space-y-0">
						<FormControl>
							<Checkbox
								checked={field.value === 'FIXED_AMOUNT'}
								onCheckedChange={(checked) => {
									if (checked) {
										field.onChange('FIXED_AMOUNT')
										if (!form.getValues('utilities.WASTE.currency')) {
											form.setValue('utilities.WASTE.currency', 'RUB')
										}
									} else {
										field.onChange(undefined)
										form.setValue('utilities.WASTE.fixedAmount', undefined)
									}
								}}
							/>
						</FormControl>
						<FormLabel className="font-[500] cursor-pointer">
							{t('properties.utilities.wasteEnabled')}
						</FormLabel>
					</FormItem>
				)}
			/>
			{enabled && (
				<div className="flex gap-2 items-start max-w-[22.4375rem]">
					<FormField
						control={control}
						name="utilities.WASTE.fixedAmount"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.utilities.amount')}</FormLabel>
								<FormControl>
									<Input type="number" placeholder="1000" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<CurrencyField label={t('properties.utilities.currency')} className="flex-1" />
				</div>
			)}
		</section>
	)
}

export function Step4Utilities() {
	const { control } = useFormContext<AddPropertyFormValues>()
	return (
		<div className="flex flex-col gap-5 px-4 pt-2 pb-8">
			<UtilitySection control={control} type="ELECTRICITY" titleKey="properties.utilities.electricity" showPaidBy />
			<UtilitySection control={control} type="WATER"       titleKey="properties.utilities.water"     showPaidBy />
			<UtilitySection control={control} type="HEATING"     titleKey="properties.utilities.heating"   showPaidBy />
			<WasteSection control={control} />
		</div>
	)
}
